import { Form, Grid, Input, TextArea } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import type { AdresseType, ErrorFieldType } from '@/types';
import RegieAdresseList from '../../RegieAdresseList/RegieAdresseList';
import RegieContactList from '../../RegieContactList/RegieContactList';
import RegieLogo from '../../RegieLogo';

interface IdentificationProps {
  identifiant: string | number;
  errors?: ErrorFieldType[];
  adresse?: AdresseType;
  collectivite?: string;
  horairesOuverture?: string;
  service?: string;
  titre?: string;
  codeInterne?: string;
  telephone?: string;
  mail?: string;
  website?: string;
  logo?: string;
  autonome?: boolean;
  onChange: (e: any, shouldChangeStatus?: boolean) => void;
}

const Identification = (props: IdentificationProps) => {
  const {
    identifiant,
    errors,
    adresse,
    collectivite,
    horairesOuverture,
    service,
    titre,
    codeInterne,
    logo,
    // informations de contact
    telephone,
    mail,
    website,
    autonome,
    onChange,
  } = props;

  const handleChange = React.useCallback(
    ({ target }) => {
      const { name, value } = target;

      if (onChange) {
        onChange({
          identifiant,
          [name]: value,
        });
      }
    },
    [identifiant, onChange],
  );

  const handleChangeCodeInterne = React.useCallback(
    ({ target: { value } }) => {
      // On autorise que les caractères alphanumériques
      if (!/^[a-zA-Z0-9]*$/.test(value)) {
        return;
      }

      if (onChange) {
        onChange({
          identifiant,
          codeInterne: _.toUpper(value),
        });
      }
    },
    [identifiant, onChange],
  );

  const handleChangeCollection = React.useCallback(
    (data) => {
      if (onChange) {
        onChange({
          identifiant,
          ...data,
        });
      }
    },
    [identifiant, onChange],
  );

  return (
    <Grid as={Form} stackable>
      <Grid.Row>
        <Grid.Column width={6}>
          <Form.Field
            id="titre"
            input={{ 'data-testid': 'identification-input-titre', maxLength: 45 }}
            required
            label={`Nom de la régie ${autonome ? 'encaissant au comptant' : 'encaissant sur facture'}`}
            name="titre"
            value={titre}
            // @ts-expect-error
            control={Input}
            fluid
            onChange={handleChange}
            error={_.some(errors, { id: 'titre' })}
          />
          <Form.Field
            id="collectivite"
            input={{ 'data-testid': 'identification-input-collectivite', maxLength: 150 }}
            required
            label="Collectivité"
            name="collectivite"
            value={collectivite}
            // @ts-expect-error
            control={Input}
            fluid
            onChange={handleChange}
            error={_.some(errors, { id: 'collectivite' })}
          />
          <Form.Field
            id="service"
            input={{ 'data-testid': 'identification-input-service', maxLength: 150 }}
            label="Service"
            name="service"
            value={service}
            // @ts-expect-error
            control={Input}
            fluid
            onChange={handleChange}
            error={_.some(errors, { id: 'service' })}
          />
          <Form.Field
            id="horairesOuverture"
            input={{ 'data-testid': 'identification-input-horaires-ouverture', maxLength: 300 }}
            label="Horaires d'ouverture"
            name="horairesOuverture"
            value={horairesOuverture || ''}
            // @ts-expect-error
            control={TextArea}
            rows={5}
            onChange={handleChange}
            error={_.some(errors, { id: 'horairesOuverture' })}
          />
        </Grid.Column>
        <Grid.Column width={1} />
        <Grid.Column width={6}>
          <Form.Field required error={_.some(errors, { id: 'codeInterne' })}>
            <label>Code interne de la régie</label>
            <input
              id="codeInterne"
              data-testid="identification-input-code-interne"
              value={codeInterne || ''}
              maxLength={5}
              onChange={handleChangeCodeInterne}
              style={{ width: 100 }}
            />
          </Form.Field>
          <RegieAdresseList adresse={adresse} onChange={handleChangeCollection} />
          <RegieContactList
            telephone={telephone}
            mail={mail}
            website={website}
            onChange={handleChangeCollection}
          />
        </Grid.Column>
        <Grid.Column width={1} />
        <Grid.Column width={2}>
          <RegieLogo identifiant={identifiant} logo={logo} titre={titre} onChange={onChange} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default Identification;
